import { useStore } from "@tanstack/react-store";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { navigationStore } from "../../../store/navigationStore";
import { usePermissions } from "../../../hooks/usePermissions";
import { useGetSubTopicById } from "../api/useGetSubTopicById";
import { useGetSubTopicIncidents } from "../api/useGetSubTopicIncidents";

export type MainTabValue = 'dossier' | 'quotes-docs' | 'journal';
export type QuotesDocsSubTab = 'quotes' | 'documents';
export type JournalSubTab = 'all' | 'incidents' | 'events' | 'communications' | 'reminders' | 'notes' | 'others';

export function useSubTopicPage() {
  const { isAdmin } = usePermissions();
  const { subTopicId, slug } = useParams();
  const lastUrl = useStore(navigationStore, (state) => state.lastUrl);

  const { data, isLoading } = useGetSubTopicById(subTopicId);
  const { data: incidents } = useGetSubTopicIncidents(subTopicId);

  const [activeTab, setActiveTab] = useState<MainTabValue>('dossier');
  const [quotesDocsSubTab, setQuotesDocsSubTab] = useState<QuotesDocsSubTab>('quotes');
  const [journalSubTab, setJournalSubTab] = useState<JournalSubTab>('all');

  const ongoingIncidentsCount = incidents?.filter(
    (i) => i.status === 'in_progress' || (i.status as string) === 'open'
  ).length ?? 0;

  const hasOngoingIncident = ongoingIncidentsCount > 0;

  function handleTabChange(_event: React.SyntheticEvent, newValue: MainTabValue) {
    setActiveTab(newValue);
  }

  function handleQuotesDocsSubTabChange(newValue: QuotesDocsSubTab) {
    setQuotesDocsSubTab(newValue);
  }

  function handleJournalSubTabChange(newValue: JournalSubTab) {
    setJournalSubTab(newValue);
  }

  function navigateToIncidents() {
    setActiveTab('journal');
    setJournalSubTab('incidents');
  }

  function navigateToQuotes() {
    setActiveTab('quotes-docs');
    setQuotesDocsSubTab('quotes');
  }

  function navigateToJournal() {
    setActiveTab('journal');
    setJournalSubTab('all');
  }

  const isSubTopicOpen = data?.state === 'pending' && !data?.archived_at;
  const fallbackBackLink = isSubTopicOpen
    ? `/spaces/${slug}`
    : `/spaces/${slug}/topics-management`;

  const backLink = lastUrl ?? fallbackBackLink;
  const parentTopicUrl = data?.topic_id
    ? `/spaces/${slug}/topics-management/${data.topic_id}`
    : null;
  const parentTopicName = data?.topics?.name ?? '';

  return {
    isAdmin,
    subTopicId,
    data,
    isLoading,
    hasOngoingIncident,
    ongoingIncidentsCount,
    activeTab,
    quotesDocsSubTab,
    journalSubTab,
    backLink,
    parentTopicUrl,
    parentTopicName,
    handleTabChange,
    handleQuotesDocsSubTabChange,
    handleJournalSubTabChange,
    navigateToIncidents,
    navigateToQuotes,
    navigateToJournal,
  };
}

